/**
 * Panel (bakelite hardware), Paper (documents, telegrams) and Stamp (rubber ink stamp).
 * `SvgDefs` must be mounted once: it provides the ink-roughening filter the stamps use.
 */
import type { CSSProperties, ReactNode } from 'react';
import { cx } from './controls';
import s from './Panel.module.css';

export function Panel({
  title,
  subtitle,
  actions,
  children,
  className,
  bodyClassName,
  scroll,
  screws = true,
  style,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  className?: string;
  bodyClassName?: string;
  scroll?: boolean;
  screws?: boolean;
  style?: CSSProperties;
}) {
  return (
    <section className={cx(s.panel, !screws && s.noScrews, className)} style={style}>
      {(title || actions) && (
        <header className={s.header}>
          <div className={s.title}>
            {title}
            {subtitle && <span className={s.subtitle}>{subtitle}</span>}
          </div>
          {actions && <div className={s.actions}>{actions}</div>}
        </header>
      )}
      <div className={cx(s.body, scroll && s.scroll, bodyClassName)}>{children}</div>
    </section>
  );
}

export function Paper({
  children,
  className,
  style,
}: {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <div className={cx(s.paper, className)} style={style}>
      {children}
    </div>
  );
}

export function Stamp({
  children,
  tone = 'red',
  rotate = -8,
  animate,
  delay,
  className,
  style,
}: {
  children: ReactNode;
  tone?: 'red' | 'green' | 'blue';
  rotate?: number;
  animate?: boolean;
  /** Animation delay in ms. */
  delay?: number;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <span
      className={cx(
        s.stamp,
        tone === 'green' && s.stampGood,
        tone === 'blue' && s.stampBlue,
        animate && s.stampAnim,
        className,
      )}
      style={
        {
          '--rot': `${rotate}deg`,
          '--delay': delay !== undefined ? `${delay}ms` : undefined,
          ...style,
        } as CSSProperties
      }
    >
      {children}
    </span>
  );
}

/** Shared SVG filters (ink bleed for stamps, paper fibre). Render once near the app root. */
export function SvgDefs() {
  return (
    <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden>
      <defs>
        <filter id="ink-rough" x="-10%" y="-20%" width="120%" height="140%">
          <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" seed="7" result="noise" />
          <feDisplacementMap
            in="SourceGraphic"
            in2="noise"
            scale="1.8"
            xChannelSelector="R"
            yChannelSelector="G"
            result="d"
          />
          <feTurbulence type="fractalNoise" baseFrequency="0.35" numOctaves="3" seed="3" result="blot" />
          <feColorMatrix
            in="blot"
            type="matrix"
            values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 -2.2 1.55"
            result="mask"
          />
          <feComposite in="d" in2="mask" operator="in" />
        </filter>
      </defs>
    </svg>
  );
}
